import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, UserPlus, MessageCircle, Trophy, Eye, BookOpen } from 'lucide-react';

const ICON_MAP = {
  follow: UserPlus,
  comment: MessageCircle,
  comment_reply: MessageCircle,
  forum_reply: MessageCircle,
  mention: MessageCircle,
  new_content: BookOpen,
  judge_assigned: Trophy,
};

const NotificationBell = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.get('/notifications/unread-count');
        setUnreadCount(res.data.count);
      } catch (e) { /* e logged */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [api]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = async () => {
    setOpen(!open);
    if (!open) {
      try {
        const res = await api.get('/notifications?limit=20');
        setNotifications(res.data);
        if (unreadCount > 0) {
          await api.post('/notifications/mark-read');
          setUnreadCount(0);
        }
      } catch (e) { /* e logged */ }
    }
  };

  const getLink = (notif) => {
    // Handle data that might be a string instead of an object
    const data = typeof notif.data === 'string' ? JSON.parse(notif.data || '{}') : (notif.data || {});
    
    if (notif.type === 'follow') return `/writer/${data.follower_id}`;
    if (notif.type === 'comment' || notif.type === 'comment_reply') 
      return `/story/${data.story_id}#comment-${data.comment_id || data.parent_id || ''}`;
    if (notif.type === 'forum_reply') 
      return `/groups/${data.group_id}?post=${data.post_id}`;
    if (notif.type === 'new_content' && data.story_id) {
      const ch = (typeof data.chapter_index === 'number' && data.chapter_index > 0) ? `?chapter=${data.chapter_index}` : '';
      return `/story/${data.story_id}${ch}`;
    }
    if (notif.type === 'mention') {
      if (data.story_id) return `/story/${data.story_id}#comment-${data.comment_id || ''}`;
      if (data.group_id) return `/groups/${data.group_id}?post=${data.post_id}`;
    }
    if (notif.type === 'collab_invite' && data.story_id) return `/story/${data.story_id}`;
    if (notif.type === 'collab_accepted' && data.story_id) return `/story/${data.story_id}`;
    if (notif.type === 'award_won' && data.story_id) return `/story/${data.story_id}`;
    if (notif.type === 'judge_assigned') return `/challenges`;
    if (notif.type === 'warning') return `/dashboard`;
    // Fallback: try to extract story_id from data
    if (data.story_id) return `/story/${data.story_id}`;
    if (data.challenge_id) return `/challenges/${data.challenge_id}`;
    if (data.group_id) return `/groups/${data.group_id}`;
    return null;
  };

  const handleNotifClick = (notif) => {
    const link = getLink(notif);
    setOpen(false);
    if (link) {
      // Small delay to ensure dropdown closes before navigation
      setTimeout(() => navigate(link), 50);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-surface-alt transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell className="w-5 h-5 text-text-secondary" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-[#E5E2DC] z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-[#E5E2DC] bg-surface-alt">
            <h3 className="font-medium text-text-primary text-sm">Notifications</h3>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = ICON_MAP[notif.type] || Bell;
                const link = getLink(notif);
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`block px-4 py-3 hover:bg-surface-alt transition-colors border-b border-[#E5E2DC]/50 ${
                      link ? 'cursor-pointer' : ''
                    } ${!notif.read ? 'bg-brand-accent/5' : ''}`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-brand-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary leading-snug">{notif.message}</p>
                        <p className="text-xs text-text-muted mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-brand-accent flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
